<script setup lang="ts">
import type { FeedbackSurveyQuestion } from '~kui/components/KUI/Unified/Completion/FeedbackSurvey/types';

import KUIUnified404 from '~kui/components/KUI/Unified/404/KUIUnified404.vue';
import KUIUniCompletionFeedbackSurvey from '~kui/components/KUI/Unified/Completion/FeedbackSurvey/KUIUniCompletionFeedbackSurvey.vue';
import KUIUniCompletion from '~kui/components/KUI/Unified/Completion/KUIUniCompletion.vue';

/**
 * The survey screen a collection and an activity both show, in the one shape.
 *
 * Three states, and the order between them is the contract: once an answer is banked the screen
 * is the thanks-and-coins overlay whatever else is true, questions come next, and a survey screen
 * reached with nothing to ask is a dead end rather than a blank panel.
 * {@see useActivitySurveyScreen} is the behaviour half of the same pair.
 */
interface Props {
  /** The questionnaire; empty means there is nothing left to ask here. */
  questions: FeedbackSurveyQuestion[];
  /** What answering paid, once banked. Null while the questionnaire is still the screen. */
  coinsAwarded: number | null;
  submitting?: boolean;
  errorMessage?: string | null;
  /** Whether a teacher reads these answers, which is what takes the skip button away. */
  sharedWithTeacher?: boolean;
  anonymousResponses?: boolean;
  isGhostUser?: boolean;
  animationsEnabled?: boolean;
  thanksHeading: string;
  /** Names the two screens for tests and for anyone reading a recording. */
  testidPrefix: string;
}

defineProps<Props>();

defineEmits<{
  submitted: [responses: Record<string, unknown>];
  skipped: [answeredCount: number];
  abandoned: [answeredCount: number];
  close: [];
}>();
</script>

<template>
  <KUIUniCompletion
    v-if="coinsAwarded !== null"
    :coins-earned="coinsAwarded"
    :animate-coins="coinsAwarded > 0"
    :progress-heading="thanksHeading"
    :is-ghost-user="!!isGhostUser"
    :animations-enabled="animationsEnabled"
    :data-testid="`${testidPrefix}-thanks`"
    @continue="$emit('close')"
  />
  <div
    v-else-if="questions.length"
    class="flex h-full flex-col justify-center p-6"
    :data-testid="`${testidPrefix}-screen`"
  >
    <KUIUniCompletionFeedbackSurvey
      :questions="questions"
      :submitting="submitting"
      :shared-with-teacher="sharedWithTeacher"
      :anonymous-responses="anonymousResponses"
      :error-message="errorMessage"
      @submitted="$emit('submitted', $event)"
      @skipped="$emit('skipped', $event)"
      @abandoned="$emit('abandoned', $event)"
    />
  </div>
  <KUIUnified404 v-else @back="$emit('close')" />
</template>
